import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

const MINT = `mutation CreateEndUserToken($input: EndUserClaimsInput!) {
  createEndUserToken(input: $input) {
    token
    expiresAt
  }
}

{
  "input": {
    "externalUserId": "user_8412",
    "name": "Ana Silva",
    "email": "ana@acme.com",
    "role": "founder",
    "plan": "growth",
    "organizationExternalId": "acct_123",
    "attributes": [
      { "key": "company_stage", "value": "seed" },
      { "key": "cohort", "value": "Spring 2026" }
    ]
  }
}`;

const SYNC = `mutation UpsertSupportUsers($users: [SupportUserInput!]!) {
  upsertSupportUsers(users: $users) {
    created
    updated
    failed
    results { index externalUserId status code message organizationLinked contactLinked }
  }
}

{
  "users": [
    {
      "externalUserId": "user_8412",
      "name": "Ana Silva",
      "email": "ana@acme.com",
      "role": "founder",
      "organizationExternalId": "acct_123",
      "attributes": [{ "key": "cohort", "value": "Spring 2026" }]
    },
    { "externalUserId": "user_9001", "name": "Ben Ode", "role": "investor" },
    { "externalUserId": "", "name": "No id" }
  ]
}`;

const SYNC_RESPONSE = `{
  "data": {
    "upsertSupportUsers": {
      "created": 2,
      "updated": 0,
      "failed": 1,
      "results": [
        { "index": 0, "externalUserId": "user_8412", "status": "CREATED", "code": null,
          "message": null, "organizationLinked": true, "contactLinked": true },
        { "index": 1, "externalUserId": "user_9001", "status": "CREATED", "code": null,
          "message": null, "organizationLinked": null, "contactLinked": false },
        { "index": 2, "externalUserId": "", "status": "FAILED", "code": "BAD_REQUEST",
          "message": "externalUserId is required",
          "organizationLinked": null, "contactLinked": null }
      ]
    }
  }
}`;

const CURL = `curl -s -X POST https://api.revvly.ai/api/v1/graphql \\
  -H "Authorization: Bearer $REVVLY_SUPPORT_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "mutation($input: EndUserClaimsInput!) { createEndUserToken(input: $input) { token expiresAt } }",
    "variables": { "input": { "externalUserId": "user_8412", "name": "Ana Silva", "email": "ana@acme.com" } }
  }'`;

const NODE_ROUTE = `// Your backend: one route your signed-in pages call to get a support token.
// The secret key stays here; the browser only ever sees the token.
app.post('/api/support-token', requireSignedInUser, async (req, res) => {
  const user = req.user; // whoever your own session says is signed in
  const response = await fetch('https://api.revvly.ai/api/v1/graphql', {
    method: 'POST',
    headers: {
      Authorization: \`Bearer \${process.env.REVVLY_SUPPORT_SECRET_KEY}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: \`mutation($input: EndUserClaimsInput!) {
        createEndUserToken(input: $input) { token expiresAt }
      }\`,
      variables: {
        input: {
          externalUserId: user.id,
          name: user.name,
          email: user.email,
          role: user.role, // your own value; your Revvly role rules decide what it grants
          organizationExternalId: user.accountId,
        },
      },
    }),
  });
  const { data, errors } = await response.json();
  if (errors) return res.status(502).json({ error: errors[0].extensions?.code });
  res.json(data.createEndUserToken); // { token, expiresAt }
});`;

const RESPONSE = `{
  "data": {
    "createEndUserToken": {
      "token": "eyJhbGciOiJSUzI1NiIsImtpZCI6...",
      "expiresAt": "2026-10-05T11:42:07.000Z"
    }
  }
}`;

const VERIFY = `import { createRemoteJWKSet, jwtVerify } from 'jose';

const JWKS = createRemoteJWKSet(new URL('https://api.revvly.ai/api/v1/support/jwks.json'));

export async function verifySupportToken(token) {
  const { payload } = await jwtVerify(token, JWKS, {
    algorithms: ['RS256'],
    issuer: 'revvly-support',
    audience: process.env.REVVLY_TENANT_ID, // required: see below
  });
  return { userId: payload.sub, role: payload.role ?? null };
}`;

const CLAIMS = `{
  "iss": "revvly-support",
  "aud": "<your tenant id>",
  "sub": "user_8412",
  "name": "Ana Silva",
  "email": "ana@acme.com",
  "role": "founder",
  "plan": "growth",
  "org": "acct_123",
  "jti": "5b0c…",
  "mk": "<id of the secret key that minted it>",
  "iat": 1791283327,
  "exp": 1791286927
}`;

export function SupportTokensGuide() {
  useDocsPage('Sign users in to the support chat — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Support chat sign-in</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Sign your users in to the <em>support chat</em>.
        </h1>
        <p className="dp-oneliner">
          Your backend vouches for the user it has already signed in, and Revvly hands back a
          short-lived token. The browser or app uses that token to chat with your support team as
          that user, and as nobody else.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="how-it-works">How it works</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">1. Your server</div>
            <div className="dp-field-v">
              Calls <span className="api-inline">createEndUserToken</span> with your support secret
              key and who the user is. The key never leaves your server.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">2. The browser or app</div>
            <div className="dp-field-v">
              Receives only the token (valid for up to one hour) from your server.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">3. The chat</div>
            <div className="dp-field-v">
              Calls the <Link to="/docs/api/support-chat">support chat API</Link> with that token to
              open requests, send messages and read replies, for that user only.
            </div>
          </div>
        </div>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Revvly never sees your users&rsquo; passwords and has no account for them. Whoever your
            backend says is signed in is who the chat talks to, so only mint a token for a user your
            own session has authenticated.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="secret-key">Create a support secret key</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Support', 'Secret keys', 'Create secret key']}
          note="Admins and owners only. The key is shown once: copy it into your server's secret store."
        />
        <p className="dp-section-sub">
          A support secret key (<span className="api-inline">rvly_sk_live_…</span>) can do one
          thing: sign your users in to the support chat. It can read your data through the public
          API but cannot change anything, so it is not the key your billing integration uses, and
          that key cannot mint support tokens. Keep it on your server only, never in a browser, a
          mobile app or a repository.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="mint">Mint a token</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">https://api.revvly.ai/api/v1/graphql</span>
          <span className="api-returns">application/json</span>
        </div>
        <p className="dp-section-sub">
          Call <span className="api-inline">createEndUserToken</span> on the public GraphQL API with
          the secret key as the Bearer token. Mint a token when the user opens the chat, not on
          every page load.
        </p>
        <span className="api-code-label">Mutation and variables</span>
        <pre className="api-code">{MINT}</pre>
        <span className="api-code-label">With curl</span>
        <pre className="api-code">{CURL}</pre>
        <span className="api-code-label">Response</span>
        <pre className="api-code">{RESPONSE}</pre>

        <h3 className="api-sub-heading">Claims you send</h3>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">externalUserId</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              Your own stable id for the user (max 200 characters). The same id always reaches the
              same requests, so never reuse one for a different person.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">name</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">Shown to your agents on the ticket (max 200).</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">email</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Where Revvly emails the user a reply they have not read in the app, after the delay
              set in Support settings. Without it, replies stay in the chat only.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">role</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              The user&rsquo;s role in your own product, as you already store it (max 64). Send it
              as it is: any value is accepted. The roles you define under Org Settings, Support,
              Roles say which values (and which attributes) they match, so a value no role matches
              simply grants no role and the user sees only articles open to everyone. Matching is
              case-insensitive, a user can have several roles, and editing a role applies to the
              next answer without a new token.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">plan</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">The user&rsquo;s plan in your product (max 64).</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">organizationExternalId</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              The user&rsquo;s account, by the <span className="api-inline">externalId</span> you
              set on the client in Revvly. When it matches, new requests are linked to that client,
              so agents see whose request it is. If that client has a contact with the same email,
              the user is linked to the contact too.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">attributes</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Up to 25 <span className="api-inline">{'{ key, value }'}</span> facts agents see
              beside the user&rsquo;s requests, such as company stage or cohort. A key starts with a
              letter and has up to 40 letters, digits, <span className="api-inline">_</span>,{' '}
              <span className="api-inline">-</span> or <span className="api-inline">.</span>; a
              value is up to 500 characters. The list you send replaces the stored one. Leave it out
              to keep what is stored, send <span className="api-inline">[]</span> to clear it.
              Revvly stores them; they are not put in the token.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">ttlSeconds</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Lifetime in seconds, clamped to 60–3600. Defaults to 3600 (one hour).
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="backend-route">Give the token to the browser</AnchorHeading>
        <p className="dp-section-sub">
          Add one route to your backend that your signed-in pages call. It looks up the user from
          your own session, mints the token and returns it. A minimal Express version:
        </p>
        <Figure
          title="Life of a support token"
          caption="Reuse a token until a minute before it expires, and mint a new one if the chat API rejects it."
        >
          <Flow
            steps={[
              { title: 'User opens the chat', text: 'Your page asks your backend for a token.' },
              {
                fork: [
                  {
                    title: 'More than a minute left',
                    text: 'Your server returns the cached token.',
                    tone: 'muted',
                  },
                  {
                    title: 'No token, or about to expire',
                    text: 'Your server mints one with the secret key.',
                    tone: 'accent',
                  },
                ],
              },
              { title: 'Chat API calls', text: 'The token is kept in memory only.' },
              {
                title: 'UNAUTHENTICATED',
                text: 'Skip your cache, mint a new token and retry once.',
                tone: 'warn',
              },
            ]}
          />
        </Figure>
        <pre className="api-code">{NODE_ROUTE}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Reuse</div>
            <div className="dp-field-v">
              Cache each user&rsquo;s token on your server, in memory or your existing cache, and
              return it until about a minute before <span className="api-inline">expiresAt</span>.
              Otherwise every page load and tab mints its own token, and a morning login rush spends
              your key&rsquo;s limit on tokens that are still valid. Key the cache on the claims you
              send, so a changed name, email or role mints a new one.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Refresh</div>
            <div className="dp-field-v">
              Fetch a new token a minute before <span className="api-inline">expiresAt</span>, or
              whenever the chat API answers <span className="api-inline">UNAUTHENTICATED</span>,
              then retry the call once. That second case should skip your cache and mint a new
              token.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Storage</div>
            <div className="dp-field-v">
              Keep it in memory. It is a credential: do not log it, put it in a URL or store it
              longer than it lives.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Sign-out</div>
            <div className="dp-field-v">
              Drop the token, in the browser and in your cache, when the user signs out of your
              product. It cannot be revoked on its own, which is why it lives an hour at most.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="sync">Sync your users in bulk (optional)</AnchorHeading>
        <p className="dp-section-sub">
          Each token you mint also updates what Revvly knows about that user. To have everyone in
          Support, End users before they first open the chat, or to update attributes without
          waiting for the next sign-in, send up to 500 users per call to{' '}
          <span className="api-inline">upsertSupportUsers</span> with the same secret key. It takes
          the same fields as the token request, without{' '}
          <span className="api-inline">ttlSeconds</span>, matches users by{' '}
          <span className="api-inline">externalUserId</span>, and never mints a token.
        </p>
        <pre className="api-code">{SYNC}</pre>
        <span className="api-code-label">Response</span>
        <pre className="api-code">{SYNC_RESPONSE}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Per user</div>
            <div className="dp-field-v">
              Each user succeeds or fails on its own, and{' '}
              <span className="api-inline">results</span> lists them in the order you sent them. A
              failed one carries <span className="api-inline">BAD_REQUEST</span> (an invalid field)
              or <span className="api-inline">CONFLICT</span> (a user Revvly anonymised), and a
              message saying what to fix. The same id twice in one call fails the second.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Links</div>
            <div className="dp-field-v">
              <span className="api-inline">organizationLinked: false</span> means no client in
              Revvly has that <span className="api-inline">externalId</span> yet.{' '}
              <span className="api-inline">contactLinked</span> says whether the user was matched to
              that client&rsquo;s contact by email.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Limits</div>
            <div className="dp-field-v">
              500 users per call; more is refused with{' '}
              <span className="api-inline">BAD_REQUEST</span>. Each call counts once against the
              key&rsquo;s rate limit, so a nightly sync of 50,000 users is 100 calls.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="revoke">Revoke and rotate</AnchorHeading>
        <p className="dp-section-sub">
          Revvly checks the minting key on every chat request, not only at mint. Revoke a secret key
          (Org Settings, Support, Secret keys) and every token it minted stops working on the next
          request, without waiting for them to expire. Rotating a key gives you a new one and keeps
          the old one working for its grace period, so you can deploy the new secret before the old
          one stops.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="errors">Errors</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">UNAUTHENTICATED</span>
            </div>
            <div className="dp-field-v">The secret key is missing, wrong, revoked or expired.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">FORBIDDEN</span>
            </div>
            <div className="dp-field-v">
              The key is not a support secret key, or Revvly Support is not enabled for your
              workspace.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">BAD_REQUEST</span>
            </div>
            <div className="dp-field-v">
              A claim is invalid: no <span className="api-inline">externalUserId</span>, a value too
              long or with control characters, an invalid email, or a
              <span className="api-inline"> ttlSeconds</span> that is not a positive whole number.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">RATE_LIMITED</span>
            </div>
            <div className="dp-field-v">
              Over the key&rsquo;s rate limit: 50 requests a second and 3,000 a minute by default,
              across all your users. Wait for the <span className="api-inline">Retry-After</span>{' '}
              seconds. If tokens are already reused and you still hit it, raise the limits under
              Settings, Support, Secret keys, Limits (up to 500 a second and 30,000 a minute), where
              you can also see the last 24 hours of traffic.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="verify">Verify a token on your side (optional)</AnchorHeading>
        <p className="dp-section-sub">
          You only need this if your own services want to accept the same token. Tokens are RS256
          JWTs; the public keys are published at{' '}
          <span className="api-inline">https://api.revvly.ai/api/v1/support/jwks.json</span>{' '}
          (cacheable for an hour, the header&rsquo;s <span className="api-inline">kid</span> names
          the key).
        </p>
        <pre className="api-code">{VERIFY}</pre>
        <div className="api-note api-note-warn">
          <span className="api-note-icon">!</span>
          <span>
            Always check <span className="api-inline">aud</span>. The same keys sign tokens for
            every Revvly workspace, so a valid signature alone would also accept another
            workspace&rsquo;s token for a user with the same id. Your tenant id is the{' '}
            <span className="api-inline">aud</span> of any token you mint: decode one once and pin
            the value in your configuration. Checking the signature also does not tell you the key
            was revoked since; only Revvly&rsquo;s own endpoints check that.
          </span>
        </div>
        <span className="api-code-label">Token payload</span>
        <pre className="api-code">{CLAIMS}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="try-it">Try it in the sandbox</AnchorHeading>
        <UiPath
          steps={['Support', 'Inbox', 'Sandbox']}
          note="Needs no secret key and touches no real customer."
        />
        <p className="dp-section-sub">
          The sandbox is a practice copy of your Support setup. Pick a practice customer (a name, a
          role value, attributes, a plan, a company), chat as them through the same API your widget
          will use, and answer from the inbox next to it. Nothing reaches your real inbox, client
          pages, metrics or email, so you can watch the unread count, notifications and replies
          behave as a customer would see them before you write any code.
        </p>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-widget">Add a support chat to your app ›</Link>
          </li>
          <li>
            <Link to="/docs/api/support-chat">Support chat API reference ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/api-keys">Configure integration API keys ›</Link>
          </li>
          <li>
            <Link to="/docs/api/graphql-public">Public GraphQL API ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
